import {
  distinctUntilChanged,
  ignoreElements,
  map,
  merge,
  tap,
  type Observable,
} from 'rxjs';
import type { ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { shallowEqual } from 'utils/objectUtils';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';

/**
 * A narrowed selection, held still until it actually differs.
 *
 * The `distinctUntilChanged` is not optional: `map` hands over a fresh object per emission, which
 * a `combineLatest` downstream reads as a change.
 */
export const toStableSelection$ = <Settings, Selection extends object>(
  viewSettings$: Observable<Settings>,
  narrow: (settings: Settings) => Selection
): Observable<Selection> =>
  viewSettings$.pipe(map(narrow), distinctUntilChanged(shallowEqual));

type DispatchingSourceData = {
  points: { componentId: string }[];
  emptyReason: string | null;
};

/**
 * The two dispatches each plotted chart view makes off its own source data, as a stream that emits nothing.
 *
 * Merged into the view model rather than run from an effect in the view, so the component is left
 * a pure function of its props and the subscription still follows its lifetime. `ignoreElements`
 * is what keeps them out of the props: they are subscriptions that happen to live on this stream,
 * not values it carries.
 */
export const toPlottedViewEffects$ = (
  sourceData$: Observable<DispatchingSourceData>,
  viewId: ViewIds
): Observable<never> =>
  merge(
    /* What "Create scenario from components in view" is built from. `viewComponentIds$` answers
       `[]` for any view that never dispatches this, and `createFromViewEnabled` requires a
       non-empty list - so without it the menu entry is offered and permanently greyed out.

       The plotted points rather than the traversal's components: these charts exclude anything
       without a value on all the required fields, and the scenario should be the picture on
       screen. */
    sourceData$.pipe(
      tap(({ points }) =>
        dispatchAction(
          readViewComponentIds({
            [viewId]: points.map(({ componentId }) => componentId),
          })
        )
      )
    ),
    /* The studio's own empty state, for the one reason it can answer better than these views can:
       an empty dataset, a filter that removed everything and a required path that matched nothing
       all arrive as "no components", and `buildEmptyStateLayoutBasedOnDataset` tells them apart
       and offers Edit dataset. The views' other reasons stay on their own doqs.

       On the reason rather than on the whole source data, so a new `points` array with the same
       verdict does not dispatch again. */
    sourceData$.pipe(
      map(({ emptyReason }) => emptyReason === 'no components'),
      distinctUntilChanged(),
      tap(isViewEmpty =>
        dispatchAction(
          updateViewEmptyStateData({
            viewEmptyStateData: { isViewEmpty },
            viewId,
          })
        )
      )
    )
  ).pipe(ignoreElements());
